// A PDF shown in the page, with buttons to open it full size and to download it.
//
// `load()` resolves to { url, downloadUrl } — addresses the browser can fetch
// itself, ending in the file's name: a short-lived signed link in the app
// (backend app/contracts/file_links.py), the token link on the public signing
// page. That is deliberate. Showing the bytes from a blob: URL instead would mean
// the viewer's own Save names the file <guid>.pdf, while our Download names it
// properly — so every route here goes through the same named address, and the
// server's Content-Disposition is the one source of the name.
//
// Embedded on wider screens; on a phone the browser's own viewer is the reliable
// way to read a multi-page PDF, so there it is the Open button alone. The buttons
// ask `load()` again rather than reusing the address, since a signed link expires.
import { useEffect, useState } from "react";
import { Alert, Box, Button, CircularProgress, Stack, useMediaQuery } from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import DownloadIcon from "@mui/icons-material/Download";

// Navigate a hidden link: the download address answers "attachment", so the
// browser saves the file and stays on the page.
export const followDownload = (url) => {
  const a = document.createElement("a");
  a.href = url;
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
};

/**
 * Props: load: () => Promise<{url, downloadUrl}>; reloadKey: reload when this
 * changes; title (for the frame); height (px).
 */
export default function PdfPreview({ load, reloadKey, title = "Document", height = 520 }) {
  const wide = useMediaQuery(theme => theme.breakpoints.up("sm"));
  const [link, setLink] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let live = true;
    setLink(null);
    setFailed(false);
    load().then(l => live && setLink(l)).catch(() => live && setFailed(true));
    return () => { live = false; };
    // `load` is a new function on every render of the caller; reloadKey says when
    // the document itself has changed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadKey]);

  if (failed) return <Alert severity="error">The document couldn't be loaded.</Alert>;
  if (!link) return <Stack sx={{ alignItems: "center", py: 4 }}><CircularProgress /></Stack>;

  // The tab is opened synchronously, inside the click, or a popup blocker would
  // stop it; the fresh address is filled in once it arrives.
  const openFull = () => {
    const tab = window.open("", "_blank");
    load().then(l => {
      if (!tab) { window.open(l.url, "_blank", "noopener"); return; }
      tab.opener = null;
      tab.location.href = l.url;
    }).catch(() => tab?.close());
  };

  const download = () => load().then(l => followDownload(l.downloadUrl)).catch(() => {});

  return (
    <Stack spacing={1}>
      {wide && (
        <Box component="iframe" src={link.url} title={title}
             sx={{ width: "100%", height, border: "1px solid", borderColor: "divider", borderRadius: 1 }} />
      )}
      <Stack direction="row" spacing={1}>
        <Button size="small" variant={wide ? "text" : "contained"} startIcon={<OpenInNewIcon />}
                onClick={openFull}>
          Open {wide ? "full size" : "document"}
        </Button>
        <Button size="small" startIcon={<DownloadIcon />} onClick={download}>Download</Button>
      </Stack>
    </Stack>
  );
}
